import { useEffect, useRef } from 'react'
import { useStore } from './state/store'
import { GenClient } from './worker/workerClient'
import { Toolbar } from './ui/Toolbar'
import { ModeRail, MODES } from './ui/ModeRail'
import { SidePanel } from './ui/SidePanel'
import { CanvasView } from './ui/CanvasView'
import { StatusBar } from './ui/StatusBar'
import { CommandPalette } from './ui/CommandPalette'
import { ExportDialog } from './ui/ExportDialog'
import { HistoryPanel } from './ui/HistoryPanel'
import { ShortcutSheet } from './ui/ShortcutSheet'
import { ShapeEditor } from './ui/ShapeEditor'
import { Gallery } from './ui/Gallery'
import { AboutDialog } from './ui/AboutDialog'
import { ScaleDialog } from './ui/ScaleDialog'
import { Onboarding } from './ui/Onboarding'
import { MobileNotice } from './ui/MobileNotice'
import { Toasts } from './ui/Toasts'
import { ErrorBoundary } from './ui/ErrorBoundary'
import { parseDocumentFile, coerceDocument } from './export/json'
import { collectAssets } from './ui/imageAssets'

const AUTOSAVE_KEY = 'ventholes-document'

function isEditable(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  )
}

export default function App() {
  const doc = useStore((s) => s.doc)
  const perfMode = useStore((s) => s.perfMode)
  const gesturing = useStore((s) => s.gesturing)
  const overlay = useStore((s) => s.overlay)
  const clientRef = useRef<GenClient | null>(null)
  const lastErrorRef = useRef<{ text: string; at: number }>({ text: '', at: 0 })

  // Apply the persisted theme to the root element once.
  useEffect(() => {
    document.documentElement.dataset.theme = useStore.getState().theme
  }, [])

  // Restore the autosaved design once.
  useEffect(() => {
    try {
      const saved = localStorage.getItem(AUTOSAVE_KEY)
      if (saved) {
        const parsed = coerceDocument(JSON.parse(saved))
        if (parsed) useStore.setState({ doc: parsed })
      }
    } catch {
      // Ignore a corrupt autosave.
    }
  }, [])

  // Generation worker wiring with supervision.
  useEffect(() => {
    const client = new GenClient()
    client.onResult = (r) => {
      useStore.getState().setResult(r)
      useStore.getState().setWorkerHealth('idle')
    }
    client.onError = (msg) => {
      console.error('generation failed:', msg)
      useStore.getState().setWorkerHealth('idle')
      const now = Date.now()
      if (msg !== lastErrorRef.current.text || now - lastErrorRef.current.at > 5000) {
        lastErrorRef.current = { text: msg, at: now }
        useStore.getState().pushToast(`Generation failed: ${msg}`, 'error')
      }
    }
    client.onRestart = (reason) => {
      useStore.getState().setWorkerHealth('restarted')
      useStore
        .getState()
        .pushToast(`Preview engine ${reason} and was restarted. Your design is intact.`, 'warn')
    }
    clientRef.current = client
    return () => client.dispose()
  }, [])

  // Regenerate on every document change. Image luminance grids are decoded
  // (and cached) on the main thread first; the sequence guard keeps a slow
  // decode from overtaking a newer request.
  const reqSeqRef = useRef(0)
  useEffect(() => {
    if (perfMode && gesturing) return // defer regeneration to gesture end
    const seq = ++reqSeqRef.current
    useStore.getState().setWorkerHealth('busy')
    collectAssets(doc).then((assets) => {
      if (reqSeqRef.current !== seq) return
      clientRef.current?.request(doc, assets)
    })
  }, [doc, perfMode, gesturing])

  // Autosave with indicator.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        localStorage.setItem(AUTOSAVE_KEY, JSON.stringify(doc))
        useStore.getState().markSaved()
      } catch {
        // Storage may be full or unavailable; autosave is best-effort.
      }
    }, 400)
    return () => clearTimeout(t)
  }, [doc])

  // Global keyboard map.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const s = useStore.getState()

      // Ctrl/Cmd combos work everywhere except while typing.
      if (e.metaKey || e.ctrlKey) {
        if (e.key.toLowerCase() === 'k') {
          e.preventDefault()
          s.setOverlay(s.overlay === 'palette' ? null : 'palette')
          return
        }
        if (isEditable(e.target)) return
        if (e.key.toLowerCase() === 'z') {
          e.preventDefault()
          if (e.shiftKey) s.redo()
          else s.undo()
        }
        if (e.key.toLowerCase() === 'y') {
          e.preventDefault()
          s.redo()
        }
        return
      }

      if (e.key === 'Escape') {
        if (s.overlay) s.setOverlay(null)
        else if (s.pendingAdd) s.setPendingAdd(null)
        else if (s.selectedController) s.setSelectedController(null)
        else if (s.selectedPath) s.setSelectedPath(null)
        return
      }

      // Plain-key shortcuts: never while typing or while an overlay is open.
      if (isEditable(e.target) || s.overlay) return

      if (e.key === '?') {
        s.setOverlay('shortcuts')
        return
      }
      const num = parseInt(e.key, 10)
      if (num >= 1 && num <= MODES.length) {
        const m = MODES[num - 1]
        s.commit(`Switch to ${m.label}`, (d) => {
          d.layout.mode = m.id
        })
        return
      }
      switch (e.key.toLowerCase()) {
        case 's':
          s.setActiveChannel('size')
          break
        case 'p':
          s.setActiveChannel('spacing')
          break
        case 'a':
          s.setActiveChannel('angle')
          break
        case 'm':
          s.setActiveChannel('shape')
          break
        case 'r':
          s.rememberSeed(s.doc.layout.seed)
          s.commit('Shuffle seed', (d) => {
            d.layout.seed = Math.floor(Math.random() * 1e9) + 1
          })
          break
        case 'e':
          s.setOverlay('export')
          break
        case 'h':
          s.setOverlay(s.overlay === 'history' ? null : 'history')
          break
        case 'f':
          s.requestFit()
          break
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Drag a previously exported SVG or JSON anywhere onto the app to restore.
  useEffect(() => {
    const onDragOver = (e: DragEvent) => e.preventDefault()
    const onDrop = async (e: DragEvent) => {
      e.preventDefault()
      const file = e.dataTransfer?.files?.[0]
      if (!file) return
      const text = await file.text()
      const parsed = parseDocumentFile(text)
      const s = useStore.getState()
      if (parsed) {
        s.loadDocument(parsed, 'Import design')
        s.pushToast('Design restored from file', 'ok')
      } else {
        s.pushToast('That file does not contain a Ventholes design', 'error')
      }
    }
    window.addEventListener('dragover', onDragOver)
    window.addEventListener('drop', onDrop)
    return () => {
      window.removeEventListener('dragover', onDragOver)
      window.removeEventListener('drop', onDrop)
    }
  }, [])

  const setOverlay = useStore((s) => s.setOverlay)

  return (
    <div className="app">
      <ErrorBoundary name="Toolbar">
        <Toolbar />
      </ErrorBoundary>
      <div className="main-row">
        <ErrorBoundary name="Mode rail">
          <ModeRail />
        </ErrorBoundary>
        <ErrorBoundary name="Canvas">
          <CanvasView />
        </ErrorBoundary>
        <ErrorBoundary name="Settings panel">
          <SidePanel />
        </ErrorBoundary>
      </div>
      <ErrorBoundary name="Status bar">
        <StatusBar />
      </ErrorBoundary>

      {overlay === 'palette' && (
        <Backdrop onClose={() => setOverlay(null)}>
          <CommandPalette />
        </Backdrop>
      )}
      {overlay === 'export' && (
        <Backdrop onClose={() => setOverlay(null)}>
          <ExportDialog />
        </Backdrop>
      )}
      {overlay === 'shortcuts' && (
        <Backdrop onClose={() => setOverlay(null)}>
          <ShortcutSheet />
        </Backdrop>
      )}
      {overlay === 'shapeEditor' && (
        <Backdrop onClose={() => setOverlay(null)}>
          <ErrorBoundary name="Shape editor">
            <ShapeEditor />
          </ErrorBoundary>
        </Backdrop>
      )}
      {overlay === 'gallery' && (
        <Backdrop onClose={() => setOverlay(null)}>
          <ErrorBoundary name="Preset gallery">
            <Gallery />
          </ErrorBoundary>
        </Backdrop>
      )}
      {overlay === 'about' && (
        <Backdrop onClose={() => setOverlay(null)}>
          <AboutDialog />
        </Backdrop>
      )}
      {overlay === 'scale' && (
        <Backdrop onClose={() => setOverlay(null)}>
          <ScaleDialog />
        </Backdrop>
      )}
      <Onboarding />
      <MobileNotice />
      {overlay === 'history' && (
        <ErrorBoundary name="History">
          <HistoryPanel />
        </ErrorBoundary>
      )}

      <Toasts />
    </div>
  )
}

function Backdrop({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      className="overlay-backdrop"
      onPointerDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      {children}
    </div>
  )
}
